/**
 * Evaluating the inspectable half of the IR: selectors, amounts and conditions.
 *
 * This module is what makes SPEC.md §5.1's promise concrete — "triggers, costs, selectors and
 * choices stay inspectable data so `legalActions` serves AI and UI through one path". A
 * target pool is computed by *reading* the IR, never by speculatively executing an effect, so
 * the option list the UI highlights and the option list the AI searches are the same array.
 *
 * ## Choice legality (CR §6.7.3)
 *
 * The CR is written as rewind semantics: make a choice, verify it against the limiters, undo
 * it if illegal. A digital engine pre-validates instead (rules inventory §10.7), and
 * `targetPool` is where that happens — it applies the selector's own limiters **and** Ward,
 * so an illegal choice is never offered and rollback never fires.
 */
import { canBeChosenBy, hasKeyword } from './keywords.ts';
import {
  costOf,
  definitionOf,
  effectiveLoreOf,
  effectiveStrengthOf,
  willpowerOf,
} from '../characteristics.ts';
import { cardNamesOf } from '../cards.ts';
import type { CardDefinition } from '../cards.ts';
import { shiftBaseSets } from '../costs.ts';
import type { GameEvent } from '../events.ts';
import type { InstanceId, PlayerIndex, Zone } from '../ids.ts';
import { grantedSubtypes, isRestricted, mustBeChosenBy } from '../modifiers.ts';
import { stackOf, underCount } from '../stacks.ts';
import type {
  CardInstance,
  GameState,
  PlayerBinding,
  ResultBinding,
  TargetBinding,
} from '../state.ts';
import {
  instanceAt,
  isStartingPlayer,
  isYourFirstTurn,
  nextPlayer,
  playerAt,
  playerBit,
  turnOrderFrom,
  zoneOf,
} from '../state.ts';
import type {
  Amount,
  CardRef,
  Condition,
  PlayerRef,
  PlayerTargetSpec,
  Selector,
  TargetSpec,
} from './ir.ts';

/**
 * Everything an IR node needs to resolve against: whose ability it is, what it is attached
 * to, what set it off, and what has been chosen so far.
 */
export interface AbilityContext {
  /** The card whose ability this is. */
  readonly source: InstanceId;
  /** Who controls the ability — the chooser for Ward, the "you" of every `PlayerRef`. */
  readonly controller: PlayerIndex;
  /** The card the triggering event was about, if any. */
  readonly subject: InstanceId | null;
  /** Targets chosen so far, by `TargetSpec.id`. */
  readonly bindings: readonly TargetBinding[];
  /** Players chosen so far, by `PlayerTargetSpec.id` (ticket 102) — `bindings`'s player twin. */
  readonly playerBindings: readonly PlayerBinding[];
  /**
   * Actual numeric results earlier steps of this same effect tree bound via `as` — what
   * `{ kind: 'bound-amount', id }` reads. Absent outside an effect frame.
   */
  readonly resultBindings?: readonly ResultBinding[];
  /**
   * The card that **caused** the triggering event, if any — what `{ kind: 'event-source' }`
   * resolves to. Carried separately from `event` because an effect resolves long after the
   * guard that saw the event: the bag item records it, exactly as it records `subject`.
   */
  readonly eventSource?: InstanceId | null;
  /**
   * The triggering event's own numeric value — its `amount`, or a `card-played` event's actual
   * paid `cost` — what `{ kind: 'triggering-amount' }` resolves to. Carried the same way as
   * `eventSource`: the bag item records it, because by the time an effect resolves the event
   * that fed it is gone from `state.events`.
   */
  readonly eventAmount?: number | null;
  /**
   * The defender of the triggering `challenge-declared`, if any — what `{ kind: 'event-defender'
   * }` resolves to. Carried the same way as `eventSource`/`eventAmount`: the bag item records
   * it, because by the time an effect resolves the challenge frame and the event that fed it are
   * both gone. `challengedCardIs`'s guard reads `ctx.event` directly instead, since a guard is
   * always evaluated at collection time while the event still exists — this field is for an
   * *effect*, which resolves later, out of the bag.
   */
  readonly eventDefender?: InstanceId | null;
  /** The triggering event, while a trigger guard is being evaluated. */
  readonly event?: GameEvent;
}

/**
 * The card an event names as its cause — the dealer of damage, the challenger behind a
 * challenge banish, the ability that chose a card. `null` when the event has none.
 */
export function eventSourceOf(event: GameEvent | undefined): InstanceId | null {
  if (event === undefined) return null;
  // A card is only ever put under another by that card's own ability (Boost, Shift — CR §8.3,
  // §8.10.4), so the pile's top card is what caused it. That is what "whenever you put a card
  // under this character" asks about, and the event names it `top` rather than `source`.
  if (event.type === 'card-put-under') return event.top;
  // "Whenever a character of yours is challenged, **the challenging character** …": the
  // declaration told from the defender's side names its challenger `by` rather than `source`,
  // but it is the card that caused the event in exactly the sense this reference means.
  if (event.type === 'card-challenged') return event.by;
  if (!('source' in event)) return null;
  return event.source;
}

/**
 * The event's own numeric value: `amount` for damage/payment events, or the ink actually paid
 * (`cost`) for `card-played`. This is what `{ kind: 'triggering-amount' }` reads in a trigger
 * guard and once the ability has left the bag. It deliberately does not recompute the played
 * card's printed/live cost after the move to play or discard.
 */
export function eventAmountOf(event: GameEvent | undefined): number | null {
  if (event === undefined) return null;
  if (event.type === 'card-played') return event.cost;
  // The {S} a banished character had in play, snapshotted before it left (ticket 104) — what
  // "banish characters with {S} equal to or less than the {S} it had" needs, since its own
  // dynamic static no longer contributes once it is out of play. Rides the same channel a
  // played card's paid cost does, for the same reason: the event that carried it is gone from
  // `state.events` by the time an effect resolves out of the bag.
  if (event.type === 'card-banished' || event.type === 'banished-in-challenge') {
    return event.effectiveStrength;
  }
  return 'amount' in event ? event.amount : null;
}

/**
 * The defender of a `challenge-declared` event — the card `{ kind: 'event-defender' }` (a
 * `CardRef`) names, the effect-side twin of `challengedCardIs`'s guard-only `Condition` of the
 * same name (CR §4.6.6, `bag.ts`'s `ChallengeParticipants`). `null` for every other event.
 */
export function eventDefenderOf(event: GameEvent | undefined): InstanceId | null {
  if (event === undefined || event.type !== 'challenge-declared') return null;
  return event.defender;
}

/** A context with no bindings — enough for guards, conditions and static reads. */
export function baseContext(
  source: InstanceId,
  controller: PlayerIndex,
  subject: InstanceId | null,
  event?: GameEvent,
): AbilityContext {
  return {
    source,
    controller,
    subject,
    bindings: [],
    playerBindings: [],
    ...(event === undefined
      ? {}
      : {
          event,
          eventSource: eventSourceOf(event),
          eventAmount: eventAmountOf(event),
          eventDefender: eventDefenderOf(event),
        }),
  };
}

// ---------------------------------------------------------------------------
// References
// ---------------------------------------------------------------------------

/**
 * Every card a reference names. A binding may name several ("banish chosen 2 characters").
 *
 * `location-of` needs the state to read the relationship, so it is resolved by
 * `resolveCardRefAllIn`; without a state it names nothing, which is the "do as much as you
 * can" answer (CR §1.2.3).
 */
export function resolveCardRefAll(ctx: AbilityContext, ref: CardRef): InstanceId[] {
  switch (ref.kind) {
    case 'instance':
      return [ref.instance];
    case 'self':
      return [ctx.source];
    case 'subject':
      return ctx.subject === null ? [] : [ctx.subject];
    case 'event-source': {
      const found = ctx.eventSource ?? eventSourceOf(ctx.event);
      return found === null ? [] : [found];
    }
    case 'event-defender': {
      const found = ctx.eventDefender ?? eventDefenderOf(ctx.event);
      return found === null ? [] : [found];
    }
    case 'location-of':
    case 'top-of-deck':
    case 'bottom-of-deck':
    // Needs the state to filter by selector (`resolveCardRefAllIn` handles it); without one,
    // "do as much as you can" names nothing (CR §1.2.3), same as `location-of`.
    case 'matching':
      return [];
    case 'target': {
      const binding = ctx.bindings.find((each) => each.id === ref.id);
      return binding === undefined ? [] : [...binding.instances];
    }
  }
}

/** `resolveCardRefAll` with the state in hand, so `location-of` can be followed. */
export function resolveCardRefAllIn(
  state: GameState,
  ctx: AbilityContext,
  ref: CardRef,
): InstanceId[] {
  if (ref.kind === 'top-of-deck') {
    const top = playerAt(state, resolvePlayerRef(state, ctx, ref.player)).deck[0];
    return top === undefined ? [] : [top];
  }
  if (ref.kind === 'bottom-of-deck') {
    const deck = playerAt(state, resolvePlayerRef(state, ctx, ref.player)).deck;
    const bottom = deck[deck.length - 1];
    return bottom === undefined ? [] : [bottom];
  }
  // The general narrowing primitive (ticket 141): everything the wrapped reference names,
  // kept only where it also satisfies the selector — a `bound(id)` multi-instance binding
  // filtered to just its character cards, or any other `CardRef`, single- or multi-instance.
  if (ref.kind === 'matching') {
    return resolveCardRefAllIn(state, ctx, ref.of).filter((id) =>
      matchesSelector(state, ctx, instanceAt(state, id), ref.is),
    );
  }
  if (ref.kind !== 'location-of') return resolveCardRefAll(ctx, ref);
  const found: InstanceId[] = [];
  for (const id of resolveCardRefAllIn(state, ctx, ref.of)) {
    const at = instanceAt(state, id).location;
    if (at !== null && !found.includes(at)) found.push(at);
  }
  return found;
}

/** The single card a reference names, or `null` when it names none. */
export function resolveCardRef(ctx: AbilityContext, ref: CardRef): InstanceId | null {
  return resolveCardRefAll(ctx, ref)[0] ?? null;
}

/** `resolveCardRef` with the state in hand — the form every state-bearing caller wants. */
export function resolveCardRefIn(
  state: GameState,
  ctx: AbilityContext,
  ref: CardRef,
): InstanceId | null {
  return resolveCardRefAllIn(state, ctx, ref)[0] ?? null;
}

export function resolvePlayerRef(
  state: GameState,
  ctx: AbilityContext,
  ref: PlayerRef,
): PlayerIndex {
  switch (ref.kind) {
    case 'player':
      return ref.player;
    case 'controller':
      return ctx.controller;
    case 'opponent':
      return nextPlayer(state, ctx.controller);
    // "Its player draws a card" — the controller of a card the effect already named. A
    // reference that resolves to nothing falls back to the ability's own controller.
    case 'card-controller': {
      const id = resolveCardRef(ctx, ref.of);
      return id === null ? ctx.controller : instanceAt(state, id).controller;
    }
    // A player bound by an earlier `choose-player` node (ticket 102), or by the current step
    // of an `each-player` loop (ticket 125). `findLast` — not `find` — because `each-player`
    // intentionally reuses one id across every player's copy of `then` (see `bind-each-player`
    // in ir.ts): the *most recently* bound entry is always the live one. `choose-player` only
    // ever binds an id once, so this changes nothing for it. Every `choose-player` pool has at
    // least one candidate in the 2-player product (SPEC.md §4.6), so this only ever names
    // nothing if `then` is read outside its own binding's scope — a card-module bug, not a
    // game state — and falls back to the controller exactly as `card-controller` does, rather
    // than throwing mid-resolution.
    case 'chosen': {
      const binding = ctx.playerBindings.findLast((each) => each.id === ref.id);
      return binding === undefined ? ctx.controller : binding.player;
    }
  }
}

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/** Whether one card satisfies a selector, in the ability's context. */
export function matchesSelector(
  state: GameState,
  ctx: AbilityContext,
  card: CardInstance,
  selector: Selector,
): boolean {
  // "An item card or a Robot character card": each branch is read on top of the enclosing
  // selector, so the zone and controller it shares are stated once.
  if (selector.or !== undefined) {
    const { or, ...shared } = selector;
    if (!or.some((each) => matchesSelector(state, ctx, card, { ...shared, ...each }))) return false;
  }
  if (card.zone !== (selector.zone ?? 'play')) return false;
  if (selector.excludeSelf === true && card.id === ctx.source) return false;
  if (selector.excludeChosen === true && isAlreadyChosen(ctx, card.id)) return false;
  // "The top N cards of your deck" — positional, so it reads the live zone array.
  if (selector.topOfDeck !== undefined) {
    const order = zoneOf(playerAt(state, card.controller), card.zone);
    const at = order.indexOf(card.id);
    if (at === -1 || at >= selector.topOfDeck) return false;
  }

  // "Chosen player's discard" (ticket 102) — a controller named by an earlier `choose-player`
  // binding, which the fixed you/opponent/any vocabulary below cannot say. Takes priority: a
  // card module never sets both on the same selector.
  if (selector.controllerRef !== undefined) {
    if (card.controller !== resolvePlayerRef(state, ctx, selector.controllerRef)) return false;
  } else {
    const controller = selector.controller ?? 'any';
    if (controller === 'you' && card.controller !== ctx.controller) return false;
    if (controller === 'opponent' && card.controller === ctx.controller) return false;
  }

  if (selector.self === true && card.id !== ctx.source) return false;
  if (selector.atLocation !== undefined && (card.location !== null) !== selector.atLocation) {
    return false;
  }
  if (selector.here === true && (card.location === null || card.location !== hereOf(state, ctx))) {
    return false;
  }
  // "…at that location" — the same question `here` asks, about a location the sentence chose.
  if (
    selector.at !== undefined &&
    (card.location === null || !resolveCardRefAllIn(state, ctx, selector.at).includes(card.location))
  ) {
    return false;
  }
  if (
    selector.looking !== undefined &&
    playerAt(state, card.controller).looking.includes(card.id) !== selector.looking
  ) {
    return false;
  }
  if (
    selector.questedThisTurn !== undefined &&
    state.questedThisTurn.includes(card.id) !== selector.questedThisTurn
  ) {
    return false;
  }
  if (
    selector.wasChallengedThisTurn !== undefined &&
    state.wasChallengedThisTurn.includes(card.id) !== selector.wasChallengedThisTurn
  ) {
    return false;
  }
  if (
    selector.cardPutUnderThisTurn !== undefined &&
    state.cardsPutUnderThisTurn.includes(card.id) !== selector.cardPutUnderThisTurn
  ) {
    return false;
  }

  if (
    selector.hasCardsUnder !== undefined &&
    underCount(state, card.id) > 0 !== selector.hasCardsUnder
  ) {
    return false;
  }
  // "Characters under this item" (The Black Cauldron) — the pile beneath a named card, read
  // the other way from `hasCardsUnder`.
  if (selector.under !== undefined) {
    const top = stackOf(state, card.id)?.top;
    if (top === undefined || !resolveCardRefAllIn(state, ctx, selector.under).includes(top)) {
      return false;
    }
  }
  // "Cards from under your characters and locations" (Visiting Christmas Past, ticket 103) —
  // filters by the pile's top card's printed type rather than a specific named top, so no
  // choice is needed to pick which piles count.
  if (selector.underType !== undefined) {
    const top = stackOf(state, card.id)?.top;
    if (top === undefined) return false;
    if (!selector.underType.includes(definitionOf(state, instanceAt(state, top)).type)) {
      return false;
    }
  }
  if (selector.challengingSource === true && !isChallengingSource(state, ctx, card)) return false;
  // "Play a character from your discard for free" — a card whose own text forbids the play is
  // not an option to begin with (CR §6.6.5). The restriction read is zone-agnostic, so it
  // answers for the discard, a hand or the top of a deck alike.
  if (selector.playable !== undefined && !isRestricted(state, card, 'play') !== selector.playable) {
    return false;
  }
  // Effect-triggered Shift uses exactly the normal Shift base search. Only singleton base
  // sets are selectable through a card target; Duo's pair is still represented by the
  // ordinary Shift payment's `onto: InstanceId[]` option.
  if (selector.shiftable !== undefined) {
    const canShift = shiftBaseSets(state, card).some((bases) => bases.length === 1);
    if (canShift !== selector.shiftable) return false;
  }
  if (selector.shiftBaseFor !== undefined) {
    const shifting = resolveCardRefAllIn(state, ctx, selector.shiftBaseFor)[0];
    if (shifting === undefined) return false;
    const legal = shiftBaseSets(state, instanceAt(state, shifting)).some(
      (bases) => bases.length === 1 && bases[0] === card.id,
    );
    if (!legal) return false;
  }
  // "…you may play that card" once the play is not free: whether its controller has the ink
  // for its cost right now (CR §4.3.3), read wherever the card is, like `playable`.
  if (
    selector.affordable !== undefined &&
    costOf(state, card) <= readyInk(state, ctx.controller) !== selector.affordable
  ) {
    return false;
  }

  const def = definitionOf(state, card);
  if (selector.types !== undefined && !selector.types.includes(def.type)) return false;
  if (
    selector.subtype !== undefined ||
    selector.anySubtype !== undefined ||
    selector.withoutSubtype !== undefined
  ) {
    // Printed classifications plus any a continuous effect granted (CR §5.3.3.2).
    const granted = grantedSubtypes(state, card);
    const subtypes = granted.length === 0 ? def.subtypes : [...def.subtypes, ...granted];
    if (selector.subtype !== undefined && !subtypes.includes(selector.subtype)) return false;
    if (
      selector.anySubtype !== undefined &&
      !selector.anySubtype.some((each) => subtypes.includes(each))
    ) {
      return false;
    }
    if (selector.withoutSubtype !== undefined && subtypes.includes(selector.withoutSubtype)) {
      return false;
    }
  }
  if (selector.name !== undefined && !def.names.includes(selector.name)) return false;
  if (
    selector.withoutName !== undefined &&
    matchesExcludedName(state, ctx, def, selector.withoutName)
  ) {
    return false;
  }
  if (selector.ink !== undefined && !def.inks.includes(selector.ink)) return false;
  if (selector.inkable !== undefined && def.inkwell !== selector.inkable) return false;
  if (selector.keyword !== undefined && !hasKeyword(state, card, selector.keyword)) return false;
  if (selector.withoutKeyword !== undefined && hasKeyword(state, card, selector.withoutKeyword)) {
    return false;
  }
  if (selector.exerted !== undefined && card.exerted !== selector.exerted) return false;
  if (selector.damaged !== undefined && card.damage > 0 !== selector.damaged) return false;

  if (selector.costAtMost !== undefined) {
    const cap =
      typeof selector.costAtMost === 'number'
        ? selector.costAtMost
        : evalAmount(state, ctx, selector.costAtMost);
    if (costOf(state, card) > cap) return false;
  }
  if (selector.costAtLeast !== undefined && costOf(state, card) < selector.costAtLeast) return false;
  // A limiter *checks* {S}, so it reads the clamped value: a −2 {S} character has "2 {S} or
  // less" and does not have "1 {S} or more" (CR §6.6.2).
  //
  // Read {S} and {W} **only when a clause actually asks for them.** These are not field
  // lookups: each resolves the card's whole static-modifier stack, and a `whileActive` static
  // whose condition counts a zone re-enters this function once per card in that zone. The
  // re-entrancy guard in `modifiers.ts` is keyed per (card, stat), so it stops a card
  // recurring into *itself* but not a descent through N distinct cards — which made an
  // unconditional read here the branching factor of a factorial. One "while you have 5 or more
  // cards in your inkwell" character (Sulley - Strategic Scarer, 3123) plus a 10-card inkwell
  // was enough to wedge a game for hours, because every candidate was priced even though no
  // selector in the cycle reads {S} at all.
  if (
    selector.strengthAtMost !== undefined ||
    selector.strengthAtLeast !== undefined ||
    selector.strengthAtMostAmount !== undefined
  ) {
    const strength = effectiveStrengthOf(state, card);
    if (selector.strengthAtMost !== undefined && strength > selector.strengthAtMost) return false;
    if (selector.strengthAtLeast !== undefined && strength < selector.strengthAtLeast) return false;
    // `strengthAtMost`'s computed-threshold twin — "…equal to or less than the {S} [something]
    // had", where the number is not a literal (Wreck-It Ralph - Raging Wrecker, ticket 104).
    if (
      selector.strengthAtMostAmount !== undefined &&
      strength > evalAmount(state, ctx, selector.strengthAtMostAmount)
    ) {
      return false;
    }
  }
  if (selector.willpowerAtMost !== undefined || selector.willpowerAtLeast !== undefined) {
    const willpower = willpowerOf(state, card);
    if (selector.willpowerAtMost !== undefined && willpower > selector.willpowerAtMost) return false;
    if (selector.willpowerAtLeast !== undefined && willpower < selector.willpowerAtLeast) {
      return false;
    }
  }
  return true;
}

function isAlreadyChosen(ctx: AbilityContext, id: InstanceId): boolean {
  return ctx.bindings.some((binding) => binding.instances.includes(id));
}

/** Whether a candidate has a literal name, or shares any base name with a referenced card. */
function matchesExcludedName(
  state: GameState,
  ctx: AbilityContext,
  candidate: CardDefinition,
  excluded: string | CardRef,
): boolean {
  if (typeof excluded === 'string') {
    return candidate.fullName === excluded || cardNamesOf(candidate).includes(excluded);
  }
  return resolveCardRefAllIn(state, ctx, excluded).some((id) => {
    const referenced = state.instances[id];
    if (referenced === undefined) return false;
    const names = cardNamesOf(definitionOf(state, referenced));
    return names.some((name) => cardNamesOf(candidate).includes(name));
  });
}

/**
 * Whether this card is the challenger of a live challenge against the ability's source
 * (CR §4.6) — read off the resolution stack, the same place Challenger's window is read from.
 */
function isChallengingSource(state: GameState, ctx: AbilityContext, card: CardInstance): boolean {
  return state.stack.some(
    (frame) =>
      frame.kind === 'challenge' && frame.challenger === card.id && frame.target === ctx.source,
  );
}

/**
 * Where "here" is for this ability (CR §5.6): the source location itself when the ability is
 * printed on a location, and otherwise the location the source character is standing at.
 * `null` when the source is nowhere, which makes every `here` selector empty.
 */
function hereOf(state: GameState, ctx: AbilityContext): InstanceId | null {
  const source = state.instances[ctx.source];
  if (source === undefined) return null;
  return definitionOf(state, source).type === 'Location' ? source.id : source.location;
}

/**
 * Every card satisfying a selector, in **turn order from the ability's controller** and then
 * in zone order — a deterministic pool, so two identical states enumerate identically.
 */
export function selectorPool(
  state: GameState,
  ctx: AbilityContext,
  selector: Selector,
): InstanceId[] {
  const zone: Zone = selector.zone ?? 'play';
  const found: InstanceId[] = [];
  for (const index of turnOrderFrom(state, ctx.controller)) {
    for (const id of zoneOf(playerAt(state, index), zone)) {
      const card = instanceAt(state, id);
      if (matchesSelector(state, ctx, card, selector)) found.push(id);
    }
  }
  return found;
}

/**
 * Zones a player may be asked to choose a card *from* today. `under` joins `play` and
 * `discard` here (ticket 63) because a faceup under-card is exactly as public as a discard —
 * `knownTo` is never touched putting one there (`stacks.ts`) — and even a **facedown**
 * under-card is safe to key by instance id: the option names *which pile*, never *what card*.
 */
const CHOOSABLE_ZONES: readonly Zone[] = ['play', 'discard', 'under'];

/**
 * The legal options for one `TargetSpec` — the selector's pool minus everything the CR's
 * limiters forbid:
 *
 * - **Ward** (CR §8.15): an opponent's card with Ward can't be chosen. The chooser is the
 *   ability's controller.
 * - already-picked cards, because one instance of "chosen" can't pick the same card twice
 *   (CR §6.1.3).
 *
 * Choosing from a **hidden** zone (a hand, a deck, an inkwell) would have to key its options
 * by `defId` to stay redaction-safe (SPEC.md §4.4) — *unless* every candidate the selector
 * would offer is already known to the **chooser** (`knownTo`, tracked per-instance). That is
 * the self-only exception ticket 77 adds: Pongo - Dear Old Dad's "look at the cards in your
 * inkwell" (`lookAt`) reveals the controller's own inkwell to them *first*, so by the time this
 * runs, an instance-id-keyed option leaks nothing the chooser did not already hold — the
 * option list is redacted from every other viewer regardless (`playerView`'s pending-options
 * rule), and the chooser's own knowledge is exactly what the printed "look at" already granted.
 *
 * This is deliberately narrow, not a general widening of the hidden-zone ban: it only ever
 * passes when the pool has already been made fully known to the chooser by an earlier step in
 * the same ability (or is empty). An unrevealed hidden zone — an opponent's hand, an
 * un-looked-at inkwell — still throws, exactly as before.
 *
 * **Mandatory target priority** (ticket 119, `StaticEffect.must-be-chosen`) narrows the
 * result last, after every other limiter has run: if any card in the already-legal pool
 * compels this `chooser` to pick it ("opponents must choose this character … if able"), the
 * pool shrinks to exactly those cards; otherwise every legal card stays, which is the "if
 * able" fallback when the priority card was never in the legal pool to begin with — Warded
 * away, excluded by the selector, already picked, or over `maxTotalStrength`. Two live
 * priority effects both survive the narrowing, the same way `legalChallengeTargets` offers
 * every Bodyguard rather than picking one.
 */
export function targetPool(
  state: GameState,
  ctx: AbilityContext,
  spec: TargetSpec,
  alreadyPicked: readonly InstanceId[] = [],
): InstanceId[] {
  const zone: Zone = spec.selector.zone ?? 'play';
  const chooser = chooserOf(state, ctx, spec);
  const pool = selectorPool(state, ctx, spec.selector);
  if (!CHOOSABLE_ZONES.includes(zone) && !pool.every((id) => knownToChooser(state, chooser, id))) {
    throw new Error(
      `Targeting the ${zone} zone needs defId-keyed options to stay redaction-safe (SPEC.md §4.4)`,
    );
  }
  const pickedStrength = alreadyPicked.reduce(
    (total, id) => total + effectiveStrengthOf(state, instanceAt(state, id)),
    0,
  );
  const legal = pool.filter((id) => {
    if (alreadyPicked.includes(id) || !canBeChosenBy(state, instanceAt(state, id), chooser)) {
      return false;
    }
    return (
      spec.maxTotalStrength === undefined ||
      pickedStrength + effectiveStrengthOf(state, instanceAt(state, id)) <= spec.maxTotalStrength
    );
  });
  const mandatory = legal.filter((id) => mustBeChosenBy(state, instanceAt(state, id), chooser));
  return mandatory.length > 0 ? mandatory : legal;
}

/** Whether `chooser` already knows this instance's identity (`CardInstance.knownTo`). */
function knownToChooser(state: GameState, chooser: PlayerIndex, id: InstanceId): boolean {
  return (instanceAt(state, id).knownTo & playerBit(chooser)) !== 0;
}

/**
 * Who answers a choice. Almost always the ability's controller; `opponent` is the "each
 * opponent chooses and banishes one of their characters" shape, where the *pool* is still read
 * relative to the controller but the decision belongs to somebody else — including Ward, which
 * asks about the **chooser** (CR §8.15), and `playerView`, which redacts a request against the
 * player it is asked of.
 */
export function chooserOf(
  state: GameState,
  ctx: AbilityContext,
  spec: { readonly chooser?: 'controller' | 'opponent' },
): PlayerIndex {
  return spec.chooser === 'opponent' ? nextPlayer(state, ctx.controller) : ctx.controller;
}

/**
 * The legal options for one `PlayerTargetSpec` (ticket 102) — `targetPool`'s player-scoped
 * counterpart. No Ward-style limiter exists over *players*, so this is just the pool: `any`
 * every player in turn order from the controller (so "you" sorts first), `opponent` every
 * other player. In the 2-player product (SPEC.md §4.6) `opponent` is always exactly one player
 * — the single-candidate rule `runNode`'s `choose-player` case follows auto-binds it, the same
 * way a `target` node with one legal card never raises a request.
 */
export function playerPool(
  state: GameState,
  ctx: AbilityContext,
  spec: PlayerTargetSpec,
): PlayerIndex[] {
  const everyone = turnOrderFrom(state, ctx.controller);
  return (spec.pool ?? 'any') === 'opponent'
    ? everyone.filter((player) => player !== ctx.controller)
    : everyone;
}

// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------

/** Compute an amount at resolution time (CR §6.1.8: counted once, as the effect resolves). */
export function evalAmount(state: GameState, ctx: AbilityContext, amount: Amount): number {
  if (typeof amount === 'number') return amount;
  switch (amount.kind) {
    case 'count':
      return selectorPool(state, ctx, amount.of).length;
    // Distinct printed inks across a pool — a dual-ink card adds both of its inks.
    case 'distinct-ink-count': {
      const inks = new Set<string>();
      for (const id of selectorPool(state, ctx, amount.of)) {
        for (const ink of definitionOf(state, instanceAt(state, id)).inks) inks.add(ink);
      }
      return inks.size;
    }
    // "Equal to their {S}" reads a characteristic, so a negative one counts as 0 (CR §6.6.2).
    case 'strength-of':
      return readCharacteristic(state, ctx, amount.of, effectiveStrengthOf);
    case 'willpower-of':
      return readCharacteristic(state, ctx, amount.of, willpowerOf);
    case 'lore-of':
      return readCharacteristic(state, ctx, amount.of, effectiveLoreOf);
    case 'cost-of':
      return readCharacteristic(state, ctx, amount.of, costOf);
    case 'damage-on': {
      const id = resolveCardRefIn(state, ctx, amount.of);
      return id === null ? 0 : instanceAt(state, id).damage;
    }
    case 'damage-total':
      return selectorPool(state, ctx, amount.of).reduce(
        (total, id) => total + instanceAt(state, id).damage,
        0,
      );
    case 'player-lore':
      return playerAt(state, resolvePlayerRef(state, ctx, amount.player)).lore;
    case 'challenges-this-turn':
      return state.challengedThisTurn.length;
    case 'cards-inked-this-turn':
      return playerAt(state, resolvePlayerRef(state, ctx, amount.player)).cardsInkedThisTurn;
    case 'cards-drawn-this-turn':
      return playerAt(state, resolvePlayerRef(state, ctx, amount.player)).cardsDrawnThisTurn;
    case 'damage-removed-this-turn':
      return amount.player === undefined
        ? state.damageRemovedThisTurn.reduce((total, removed) => total + removed, 0)
        : (state.damageRemovedThisTurn[resolvePlayerRef(state, ctx, amount.player)] ?? 0);
    case 'cards-put-into-discard-this-turn':
      return playerAt(state, resolvePlayerRef(state, ctx, amount.player)).cardsPutIntoDiscardThisTurn;
    case 'cards-discarded-this-turn':
      return playerAt(state, resolvePlayerRef(state, ctx, amount.player)).cardsDiscardedThisTurn;
    case 'played-this-turn-count':
      return state.playedThisTurn.filter((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesPlayed(state, ctx, card, amount.of);
      }).length;
    // Matched like `played-this-turn-count`: the banished card is in a discard by now.
    case 'banished-in-challenge-this-turn-count':
      return state.banishedInChallengeThisTurn.filter((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesPlayed(state, ctx, card, amount.of);
      }).length;
    case 'minus':
      return -evalAmount(state, ctx, amount.of);
    case 'times':
      return amount.n * evalAmount(state, ctx, amount.of);
    case 'plus':
      return evalAmount(state, ctx, amount.a) + evalAmount(state, ctx, amount.b);
    case 'cards-under': {
      const id = resolveCardRefIn(state, ctx, amount.of);
      return id === null ? 0 : underCount(state, id);
    }
    case 'bound-count':
      return ctx.bindings.find((each) => each.id === amount.id)?.instances.length ?? 0;
    case 'triggering-amount':
      return ctx.eventAmount ?? 0;
    case 'bound-amount':
      return ctx.resultBindings?.find((each) => each.id === amount.id)?.amount ?? 0;
    case 'own-move-cost': {
      const source = state.instances[ctx.source];
      return source === undefined ? 0 : (definitionOf(state, source).moveCost ?? 0);
    }
    case 'amount-at-most':
      return Math.min(evalAmount(state, ctx, amount.of), evalAmount(state, ctx, amount.max));
  }
}

function readCharacteristic(
  state: GameState,
  ctx: AbilityContext,
  ref: CardRef,
  read: (state: GameState, card: CardInstance) => number,
): number {
  const id = resolveCardRefIn(state, ctx, ref);
  return id === null ? 0 : read(state, instanceAt(state, id));
}

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

/** Evaluate a condition. Event-scoped members are false outside a trigger guard. */
export function evalCondition(
  state: GameState,
  ctx: AbilityContext,
  condition: Condition,
): boolean {
  switch (condition.kind) {
    case 'always':
      return true;
    case 'not':
      return !evalCondition(state, ctx, condition.of);
    case 'all':
      return condition.of.every((each) => evalCondition(state, ctx, each));
    case 'any':
      return condition.of.some((each) => evalCondition(state, ctx, each));
    case 'count-at-least':
      return selectorPool(state, ctx, condition.of).length >= condition.n;
    case 'count-at-most':
      return selectorPool(state, ctx, condition.of).length <= condition.n;
    case 'count-more-than':
      return (
        selectorPool(state, ctx, condition.of).length >
        selectorPool(state, ctx, condition.than).length
      );
    case 'amount-more-than':
      return evalAmount(state, ctx, condition.of) > evalAmount(state, ctx, condition.than);
    // Max-vs-max over two pools (ticket 84), re-read on every call — never cached. Mirrors
    // Flynn Rider - Frenemy's custom() logic (selectors.ts has no `readAmount`/`readPool` bag
    // ceremony to worry about here since this runs synchronously against live state).
    case 'strength-exceeds-each': {
      const of = selectorPool(state, ctx, condition.of);
      if (of.length === 0) return false;
      const than = selectorPool(state, ctx, condition.than);
      if (than.length === 0) return true;
      let bestOf = -Infinity;
      for (const id of of) {
        const strength = effectiveStrengthOf(state, instanceAt(state, id));
        if (strength > bestOf) bestOf = strength;
      }
      let bestThan = -Infinity;
      for (const id of than) {
        const strength = effectiveStrengthOf(state, instanceAt(state, id));
        if (strength > bestThan) bestThan = strength;
      }
      return bestOf > bestThan;
    }
    case 'card-matches': {
      const id = resolveCardRefIn(state, ctx, condition.card);
      if (id === null) return false;
      return matchesSelector(state, ctx, instanceAt(state, id), condition.is);
    }
    // "If any of the cards that were under them are character cards" — `card-matches`'s "any
    // of" form, for a reference that can name several cards at once (ticket 141).
    case 'any-card-matches':
      return resolveCardRefAllIn(state, ctx, condition.card).some((id) =>
        matchesSelector(state, ctx, instanceAt(state, id), condition.is),
      );
    case 'your-turn':
      return state.activePlayer === ctx.controller;
    case 'your-first-turn':
      return isYourFirstTurn(state, ctx.controller);
    case 'starting-player':
      return isStartingPlayer(state, ctx.controller);
    case 'event-player-is-opponent': {
      const event = ctx.event;
      if (event === undefined || !('player' in event)) return false;
      return event.player !== ctx.controller;
    }
    case 'event-source-type': {
      const source = ctx.eventSource ?? eventSourceOf(ctx.event);
      if (source === null) return false;
      const type = definitionOf(state, instanceAt(state, source)).type;
      return condition.types.includes(type);
    }
    case 'event-in-challenge': {
      const event = ctx.event;
      if (event === undefined) return false;
      if (event.type !== 'card-banished' && event.type !== 'damage-dealt') return false;
      if (event.challengeRole === null) return false;
      return condition.role === undefined || condition.role === event.challengeRole;
    }
    case 'event-source-is': {
      const source = ctx.eventSource ?? eventSourceOf(ctx.event);
      if (source === null) return false;
      return resolveCardRefAllIn(state, ctx, condition.card).includes(source);
    }
    case 'event-ability-is': {
      const event = ctx.event;
      if (event === undefined) return false;
      // Which ability chose a card (Support's own choice) reads `card-chosen`'s label; which
      // ability activated (ticket 104's "whenever you use the Boost ability of a character")
      // reads `ability-activated`'s — a card's derived Boost ability lands at a different index
      // per card, but its printed name does not.
      if (event.type === 'card-chosen') return event.ability === condition.name;
      if (event.type === 'ability-activated') return event.label === condition.name;
      return false;
    }
    case 'event-location-is': {
      const event = ctx.event;
      if (event === undefined || !('location' in event) || event.location === null) return false;
      return resolveCardRefAllIn(state, ctx, condition.card).includes(event.location);
    }
    // "…from a location": the mover's *prior* location, which is gone from the board the
    // moment the move lands, so it can only be read off the event that carried it.
    case 'event-moved-from': {
      const event = ctx.event;
      if (event === undefined || event.type !== 'character-moved' || event.from === null) {
        return false;
      }
      if (condition.is === undefined) return true;
      return matchesSelector(state, ctx, instanceAt(state, event.from), condition.is);
    }
    // "Return … from your discard" (ticket 76): the zone a `card-returned` event's card left,
    // read straight off the event rather than the board — by trigger-collection time the card
    // is already sitting in the hand it returned to.
    case 'event-returned-from': {
      const event = ctx.event;
      if (event === undefined || event.type !== 'card-returned') return false;
      return event.from === condition.zone;
    }
    case 'event-role': {
      const event = ctx.event;
      if (event === undefined || !('role' in event)) return false;
      return event.role === condition.role;
    }
    // "If it had a card under it": the event's own snapshot, taken before `dissolveStack`
    // scattered the pile (`zones.ts`'s `banish`) — the board itself always reads empty by now.
    case 'event-had-cards-under': {
      const event = ctx.event;
      if (event === undefined || !('hadCardsUnder' in event)) return false;
      return event.hadCardsUnder;
    }
    // "If there was an exerted character here": `card-banished`'s own snapshot, taken before
    // `moveInstance` clears every occupant's `location` the same instant it clears the
    // location's own (The Frozen Vine - Monstrous Plant, ticket 104) — the board reads empty
    // by trigger-collection time exactly as `hadCardsUnder`'s pile does.
    case 'event-had-exerted-character-here': {
      const event = ctx.event;
      if (event === undefined || !('hadExertedCharacterHere' in event)) return false;
      return event.hadExertedCharacterHere;
    }
    case 'event-other-is-self': {
      const event = ctx.event;
      if (event === undefined || !('by' in event)) return false;
      return event.by === ctx.source;
    }
    case 'event-other-is': {
      const event = ctx.event;
      if (event === undefined || !('by' in event)) return false;
      return matchesSelector(state, ctx, instanceAt(state, event.by), condition.is);
    }
    case 'amount-equals':
      return evalAmount(state, ctx, condition.of) === condition.n;
    // "While challenging a damaged character" — read off the resolution stack, so it needs no
    // state field and no cleanup; it stops being true the moment the challenge frame pops.
    case 'challenging': {
      const frame = state.stack.find(
        (each) => each.kind === 'challenge' && each.challenger === ctx.source,
      );
      if (frame === undefined || frame.kind !== 'challenge' || frame.target === null) return false;
      return matchesSelector(state, ctx, instanceAt(state, frame.target), condition.is);
    }
    case 'event-defender': {
      const event = ctx.event;
      if (event === undefined || event.type !== 'challenge-declared') return false;
      return matchesSelector(state, ctx, instanceAt(state, event.defender), condition.is);
    }
    case 'event-banisher': {
      const source = ctx.eventSource ?? eventSourceOf(ctx.event);
      if (source === null) return false;
      return matchesSelector(state, ctx, instanceAt(state, source), condition.is);
    }
    case 'bound-at-least': {
      const binding = ctx.bindings.find((each) => each.id === condition.id);
      return (binding?.instances.length ?? 0) >= condition.n;
    }
    case 'played-this-turn':
      return state.playedThisTurn.some((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesPlayed(state, ctx, card, condition.of);
      });
    // "If an opposing character was banished in a challenge this turn" — the same printed-
    // characteristics matching as `played-this-turn`: the card is in a discard by now.
    case 'banished-in-challenge-this-turn':
      return state.banishedInChallengeThisTurn.some((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesPlayed(state, ctx, card, condition.of);
      });
    // "If a character was banished this turn" — any cause, matched like
    // `banished-in-challenge-this-turn`: the card is in a discard by now.
    case 'banished-this-turn':
      return state.banishedThisTurn.some((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesPlayed(state, ctx, card, condition.of);
      });
    // "Unless one of their characters has challenged this turn" — the challenger side of
    // `GameState.challengedThisTurn`, matched like `played-this-turn`: a challenger banished
    // in its own challenge is in a discard by now, and it challenged all the same.
    case 'challenged-this-turn':
      return state.challengedThisTurn.some((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesPlayed(state, ctx, card, condition.of);
      });
    // "If 1 or more cards have left a player's discard this turn" — matched like
    // `played-this-turn`: whatever left is wherever it landed by the time anything asks.
    case 'left-discard-this-turn':
      return state.leftDiscardThisTurn.some((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesPlayed(state, ctx, card, condition.of);
      });
    // "If one of your characters took damage this turn" / "if an opposing character took
    // damage this turn" — matched like `banished-this-turn`, over the pool of everything that
    // has taken damage this turn rather than one bound card.
    case 'damaged-this-turn':
      return state.damagedThisTurn.some((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesPlayed(state, ctx, card, condition.of);
      });
    // Whose ink is counted: the ability's controller unless the sentence names somebody else
    // ("…unless their player pays 3 {I}").
    case 'can-pay-ink':
      return (
        readyInk(
          state,
          condition.player === undefined
            ? ctx.controller
            : resolvePlayerRef(state, ctx, condition.player),
        ) >= evalAmount(state, ctx, condition.amount)
      );
  }
}

/**
 * A card **played** this turn is matched on its printed characteristics and its controller,
 * never on its zone: by the time anything asks, an Action is already in the discard.
 */
function matchesPlayed(
  state: GameState,
  ctx: AbilityContext,
  card: CardInstance,
  selector: Selector,
): boolean {
  const controller = selector.controller ?? 'any';
  if (controller === 'you' && card.controller !== ctx.controller) return false;
  if (controller === 'opponent' && card.controller === ctx.controller) return false;
  const def = definitionOf(state, card);
  if (selector.types !== undefined && !selector.types.includes(def.type)) return false;
  if (selector.subtype !== undefined && !def.subtypes.includes(selector.subtype)) return false;
  if (
    selector.withoutSubtype !== undefined &&
    def.subtypes.includes(selector.withoutSubtype)
  ) {
    return false;
  }
  if (selector.name !== undefined && !def.names.includes(selector.name)) return false;
  if (
    selector.withoutName !== undefined &&
    matchesExcludedName(state, ctx, def, selector.withoutName)
  ) {
    return false;
  }
  return true;
}

/** Ready ink cards a player has — `zones.ts`'s `availableInk`, without closing an import cycle. */
function readyInk(state: GameState, player: PlayerIndex): number {
  let ready = 0;
  for (const id of playerAt(state, player).inkwell) {
    if (!instanceAt(state, id).exerted) ready += 1;
  }
  return ready;
}
